import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import apiClient from '@/utils/api';
import { sectorName } from '@/hooks/useSectors';
import { SectorAlertDialog } from '@/components/SectorAlertDialog';

export const PopularSectors = () => {
  const { t, i18n } = useTranslation();
  const [sectors, setSectors] = useState([]);
  const [alertFor, setAlertFor] = useState(null);
  const lang = (i18n.language || 'pt').slice(0, 2);

  useEffect(() => {
    apiClient.get('/categories/popular', { params: { limit: 12 } })
      .then((r) => setSectors(r.data?.items || []))
      .catch(() => setSectors([]));
  }, []);

  if (sectors.length === 0) return null;

  return (
    <section aria-labelledby="sectors-title" className="border-t border-slate-100 bg-white" data-testid="popular-sectors-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10 sm:mb-12">
          <div>
            <p className="text-xs font-semibold tracking-[0.10em] uppercase text-blue-700 mb-3">{t('home.sectorsEyebrow')}</p>
            <h2 id="sectors-title" className="text-[26px] sm:text-3xl lg:text-[36px] font-bold text-slate-900 tracking-tighter leading-[1.15]">
              {t('home.sectorsTitle')}
            </h2>
            <p className="mt-3 text-sm text-slate-500">{t('home.sectorsAlertHint')}</p>
          </div>
          <Link to="/jobs/search" className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-900" data-testid="sectors-see-all">
            {t('home.sectorsSeeAll')}
            <Icons.ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </header>

        <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {sectors.map((s) => {
            const Icon = Icons[s.icon] || Icons.Briefcase;
            const label = sectorName(s, lang);
            return (
              <li key={s.slug} className="relative group rounded-xl border border-slate-200 bg-white hover:border-slate-900 hover:shadow-sm transition-[border-color,box-shadow,transform] hover:-translate-y-0.5">
                <Link to={`/jobs/search?sector=${s.slug}`} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-5 pr-12" data-testid={`sector-tile-${s.slug}`}>
                  <span className="shrink-0 h-10 w-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center group-hover:bg-slate-900 group-hover:text-white transition-colors">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm sm:text-[15px] font-semibold text-slate-900 leading-snug">{label}</span>
                    <span className={`block mt-1 text-xs ${s.job_count > 0 ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`} data-testid={`sector-tile-count-${s.slug}`}>
                      {s.job_count > 0 ? t('home.sectorsJobCount', { count: s.job_count }) : t('home.sectorsExplore')}
                    </span>
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => setAlertFor({ slug: s.slug, label })}
                  className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-slate-400 hover:text-blue-700 hover:bg-blue-50 transition-colors"
                  title={t('home.sectorsAlertButton')}
                  aria-label={t('home.sectorsAlertButton')}
                  data-testid={`sector-alert-btn-${s.slug}`}
                >
                  <Icons.Bell className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <SectorAlertDialog sector={alertFor?.slug} sectorLabel={alertFor?.label} open={!!alertFor} onOpenChange={(v) => { if (!v) setAlertFor(null); }} />
    </section>
  );
};
